import {
  Chess,
  Swal,
  copyText,
  createPlayableBoard,
  formatClock,
  gameResultText,
  refreshPlayableBoard,
  renderMoveList
} from './chess-ui.js';
import { validatePosition } from './position-validation.js';
import { playMoveFeedback } from './move-feedback.js';

const root = document.querySelector('[data-local-game]');
if (root) {
  const requestedFen = root.dataset.initialFen?.trim();
  let startingFen = new Chess().fen();
  let rejectedFenMessage = '';
  if (requestedFen) {
    const validation = validatePosition(requestedFen);
    if (validation.isLegal && validation.canStart) {
      startingFen = validation.normalizedFen;
    } else {
      rejectedFenMessage = validation.errors[0] ?? validation.warnings[0] ?? 'پوزیسیون قابل شروع نیست.';
    }
  }
  const game = new Chess(startingFen);
  const movesElement = root.querySelector('[data-move-list]');
  const statusElement = root.querySelector('[data-game-status]');
  const boardArea = root.querySelector('[data-local-board-area]');
  const whiteClockElement = root.querySelector('[data-clock="white"]');
  const blackClockElement = root.querySelector('[data-clock="black"]');
  const initialSelect = root.querySelector('[data-initial-time]');
  const incrementSelect = root.querySelector('[data-increment]');
  let initialMilliseconds = Number(initialSelect.value) * 1000;
  let incrementMilliseconds = Number(incrementSelect.value) * 1000;
  let whiteMilliseconds = initialMilliseconds;
  let blackMilliseconds = initialMilliseconds;
  let clockRunning = false;
  let lastTick = performance.now();
  let orientation = 'white';
  let clockFrame;

  const ground = createPlayableBoard(document.querySelector('#localBoard'), game, async move => {
    playMoveFeedback();
    if (!clockRunning) startClock();
    if (move.color === 'w') whiteMilliseconds += incrementMilliseconds;
    else blackMilliseconds += incrementMilliseconds;
    updateInterface([move.from, move.to]);
    if (game.isGameOver()) {
      stopClock();
      await Swal.fire({ icon: 'info', title: 'پایان بازی', text: gameResultText(game), confirmButtonText: 'باشه' });
    }
  });

  root.querySelector('[data-start-clock]').addEventListener('click', startClock);
  root.querySelector('[data-flip-board]').addEventListener('click', flipBoard);
  root.querySelector('[data-undo]').addEventListener('click', undoMove);
  root.querySelector('[data-reset]').addEventListener('click', resetGame);
  document.querySelector('[data-new-local-game]')?.addEventListener('click', resetGame);
  root.querySelector('[data-copy-pgn]').addEventListener('click', () => copyText(game.pgn(), 'PGN بازی کپی شد'));

  initialSelect.addEventListener('change', () => {
    if (game.history().length > 0) return;
    initialMilliseconds = Number(initialSelect.value) * 1000;
    whiteMilliseconds = initialMilliseconds;
    blackMilliseconds = initialMilliseconds;
    updateClocks();
  });
  incrementSelect.addEventListener('change', () => {
    incrementMilliseconds = Number(incrementSelect.value) * 1000;
  });

  function startClock() {
    if (clockRunning || game.isGameOver()) return;
    clockRunning = true;
    lastTick = performance.now();
    tick();
  }

  function tick(now = performance.now()) {
    if (!clockRunning) return;
    const elapsed = now - lastTick;
    lastTick = now;
    if (game.turn() === 'w') whiteMilliseconds -= elapsed;
    else blackMilliseconds -= elapsed;
    updateClocks();

    if (whiteMilliseconds <= 0 || blackMilliseconds <= 0) {
      stopClock();
      const winner = whiteMilliseconds <= 0 ? 'سیاه' : 'سفید';
      statusElement.textContent = `زمان تمام شد؛ ${winner} برنده است`;
      refreshPlayableBoard(ground, game, false);
      Swal.fire({ icon: 'info', title: 'زمان تمام شد', text: `${winner} برنده بازی است.`, confirmButtonText: 'باشه' });
      return;
    }
    clockFrame = requestAnimationFrame(tick);
  }

  function stopClock() {
    clockRunning = false;
    cancelAnimationFrame(clockFrame);
  }

  function undoMove() {
    const move = game.undo();
    if (!move) return;
    stopClock();
    refreshPlayableBoard(ground, game, true);
    updateInterface();
  }

  async function resetGame() {
    if (game.history().length > 0) {
      const result = await Swal.fire({ title: 'بازی جدید؟', text: 'حرکت‌های فعلی پاک می‌شوند.', icon: 'question', showCancelButton: true, confirmButtonText: 'بله، شروع کن', cancelButtonText: 'انصراف' });
      if (!result.isConfirmed) return;
    }
    stopClock();
    game.load(startingFen);
    initialMilliseconds = Number(initialSelect.value) * 1000;
    incrementMilliseconds = Number(incrementSelect.value) * 1000;
    whiteMilliseconds = initialMilliseconds;
    blackMilliseconds = initialMilliseconds;
    refreshPlayableBoard(ground, game, true);
    updateInterface();
  }

  function flipBoard() {
    orientation = orientation === 'white' ? 'black' : 'white';
    boardArea.classList.toggle('orientation-black', orientation === 'black');
    ground.set({ orientation });
  }

  function updateInterface(lastMove) {
    refreshPlayableBoard(ground, game, !game.isGameOver(), lastMove);
    renderMoveList(movesElement, game.history({ verbose: true }));
    statusElement.textContent = gameResultText(game);
    updateClocks();
  }

  function updateClocks() {
    whiteClockElement.textContent = formatClock(whiteMilliseconds);
    blackClockElement.textContent = formatClock(blackMilliseconds);
    whiteClockElement.classList.toggle('active', game.turn() === 'w' && !game.isGameOver());
    blackClockElement.classList.toggle('active', game.turn() === 'b' && !game.isGameOver());
    whiteClockElement.classList.toggle('low-time', whiteMilliseconds < 10_000 && !game.isGameOver());
    blackClockElement.classList.toggle('low-time', blackMilliseconds < 10_000 && !game.isGameOver());
  }

  updateInterface();

  if (rejectedFenMessage) {
    Swal.fire({
      icon: 'warning',
      title: 'پوزیسیون سفارشی شروع نشد',
      text: rejectedFenMessage,
      confirmButtonText: 'باشه'
    });
  }
}
